import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, InputBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useDebounce } from 'react-use';

type SearchBarProps = {
  initialValue: string;
  onChange: (newSearchVal: string) => void;
};

// Main component
export const SearchBar: React.FC<SearchBarProps> = ({
  initialValue,
  onChange,
}) => {
  const [val, setVal] = useState<string>(initialValue);

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setVal(event.target.value);
  };

  useDebounce(
    () => {
      onChange(val);
    },
    400,
    [val]
  );

  const handleClickClearSearch = () => setVal('');
  const handleMouseDownClearSearch = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Search sx={{ width: '100%', maxWidth: '25rem', m: '0.3rem' }}>
      <StyledInputBase
        placeholder='Search…'
        inputProps={{ 'aria-label': 'search' }}
        value={val}
        onChange={handleChange}
        fullWidth
        sx={{
          width: '100%',
          color: (theme) => theme.colors.common.white,
          background: (theme) => theme.colors.darkTheme.grey.medium,
          border: (theme) => `1px solid ${theme.colors.darkTheme.grey.muted}`,
          borderRadius: (theme) => theme.borderRadius.primary,
        }}
        startAdornment={
          <InputAdornment position='start'>
            <SearchIconWrapper>
              <SearchIcon
                sx={{
                  fill: (theme) => theme.colors.common.softWhite,
                }}
              />
            </SearchIconWrapper>
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position='end'>
            <IconButton
              aria-label='Clear Search'
              onClick={handleClickClearSearch}
              onMouseDown={handleMouseDownClearSearch}
              edge='end'
              sx={{ mr: '0.2rem' }}
            >
              <HighlightOffIcon
                sx={{ color: (theme) => theme.colors.darkTheme.grey.muted }}
              />
            </IconButton>
          </InputAdornment>
        }
      />
    </Search>
  );
};

/* Styled Components */

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}));
